import { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import useMfa from 'api/useMfa';
import usePermissions from 'api/usePermissions';
import useUsers from 'api/useUsers';
import { EPublisherSolutionModelSubType } from 'common/contracts';
import { EDashboardPage, ELocalStorageKeys, ELogoutMessage } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { useLogin } from 'hooks/useLogin';
import useUrlQuery from 'hooks/useUrlQuery';
import { authActions } from 'store/authSlice';
import { localStorageUtil } from 'utils/localStorageUtil';
import { permissionsUtil } from 'utils/permissionsUtil';
import { v4 as uuidv4 } from 'uuid';
import { SubmitTokenPasswordResetParams } from 'views/Profile/ProfileView.types';

import { readHttpErrorMessageFromUnknown } from '../../utils/httpErrorHelpers';
import { EToasterStatus, showToast } from '../../utils/showToast';
import { FinishAccountForm } from '../FinishAccountForm/FinishAccountForm';
import ForgotPasswordForm from '../ForgotPasswordForm/ForgotPasswordForm';
import LoginForm from '../LoginForm/LoginForm';
import LockoutScreen from '../mfa/LockoutScreen/LockoutScreen';
import MfaVerificationForm from '../mfa/VerificationForm/VerificationForm';
import { ResetPasswordForm } from '../ResetPasswordForm/ResetPasswordForm';

import { useLockoutHandler } from './hooks/use-lockout-handler';
import { useMfaFlow } from './hooks/use-mfa-flow';
import {
  ELockoutType,
  ELoginAuthMethod,
  EModalState,
  LoginData,
  LoginNotificationData,
  LoginPayload
} from './LoginModal.types';

import 'animate.css';
import './style.scss';

const LoginModal = () => {
  const mode = useUrlQuery('mode') as EModalState;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { saveLoginDetails, isLoggedIn, cleanUserLoginData } = useLogin();
  const {
    forgotPassword,
    resetPassword,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithToken
  } = useUsers({});
  const { authenticateMfa } = useMfa();
  const [modalState, setModalState] = useState<EModalState>(mode || EModalState.LOGIN);
  const { getPermissions } = usePermissions();
  const { trackPreLoginEvent } = useBigQueryEventsTracking();
  const trackedPagesRef = useRef<Set<string>>(new Set());

  const setNotificationHandler = useCallback(
    (data: LoginNotificationData): void => {
      showToast({
        message: data.message,
        status: data.type
      });
    },
    []
  );

  const setModalStateHandler = useCallback((state: EModalState): void => {
    setModalState(state);
  }, []);

  const handleLoginSuccess = useCallback(
    (loginResponse: any, rememberMe: boolean, loginData?: LoginData): void => {
      const data = loginResponse?.result || loginResponse;
      const token = data?.token || loginData?.accessToken;
      
      if (!data?.userId) {
        showToast({
          message:
            'Seems like you are using an outdated version of the app. Please contact Appcharge support.',
          status: EToasterStatus.ERROR
        });
        cleanUserLoginData(ELogoutMessage.OUTDATED_VERSION);
        return;
      }

      const loginPayload: LoginPayload = { userId: data.userId };
      if (token) loginPayload.authJwt = token;
      dispatch(authActions.login(loginPayload));

      saveLoginDetails({
        email: data.email,
        firstName: data.userName,
        lastName: '',
        userId: data.userId,
        isSession: !rememberMe,
        token: token,
        role: data?.role,
        typeId: data?.typeId,
        projects: data?.projects,
        portals: data?.portals,
        version: data?.version,
        company: data?.company,
        companyDisplayName: data?.companyDisplayName,
        isOnboarding: data?.isOnboarding
      });

      trackPreLoginEvent({
        page: EDashboardPage.DASHBOARD,
        userId: data.userId,
        userEmail: data.email
      });

      if (loginData?.isZendesk && loginData?.returnTo && data.zendeskToken) {
        const zendeskJwtUrl = `https://appchargehelp.zendesk.com/access/jwt?jwt=${data.zendeskToken}&return_to=${encodeURIComponent(loginData.returnTo)}`;

        try {
          window.location.href = zendeskJwtUrl;
          return;
        } catch (error) {
          console.error(
            '❌ Zendesk JWT redirect failed, redirecting to dashboard:',
            error
          );
          window.location.href = '/dashboard';
          return;
        }
      }

      if (loginData?.isZendesk && loginData?.returnTo && !data.zendeskToken) {
        localStorageUtil.set(
          ELocalStorageKeys.ZENDESK_TOKEN,
          data.zendeskToken
        );
        console.warn(
          '⚠️ Zendesk login but no JWT token, redirecting to dashboard'
        );
        window.location.href = '/dashboard';
        return;
      }

      const isOrdersOrPayoutsEnabled =
        permissionsUtil.isAnyOrdersOrPayoutsEnabled(getPermissions());
      const isSingleProject = data?.projects?.length === 1;
      const isPaymentLinkProject =
        data?.projects?.[0]?.projectSubType ===
        EPublisherSolutionModelSubType.PAYMENT_LINKS;

      const getDefaultPath = () => {
        if (isSingleProject && isPaymentLinkProject) {
          const publisherId = data?.projects?.[0]?.publisherId;
          return publisherId ? `/project/${publisherId}/settings` : '';
        }
        if (isOrdersOrPayoutsEnabled) return '/projects';
        return '/projects';
      };

      const defaultPath = getDefaultPath();
      const fromPath = location.state?.from?.pathname;
      const navigationPath =
        fromPath && fromPath !== '/'
          ? `${fromPath}${location.state.from.search || ''}`
          : defaultPath;

      navigate(navigationPath, { replace: true });
    },
    [
      dispatch,
      cleanUserLoginData,
      saveLoginDetails,
      location.state,
      navigate,
      getPermissions,
      trackPreLoginEvent
    ]
  );

  const [lockoutState, setLockoutState] = useState<{
    endTime: Date | null;
    type: ELockoutType | null;
  }>({
    endTime: null,
    type: null
  });

  const { handleRateLimitError, handleLockoutEnd } = useLockoutHandler({
    setLockout: setLockoutState,
    setModalState,
    trackPreLoginEvent
  });

  const mfaFlow = useMfaFlow({
    onLoginSuccess: handleLoginSuccess,
    onRateLimitError: handleRateLimitError
  });

  const handleContactSupport = useCallback((): void => {
    window.location.href = 'mailto:support@appcharge-b2b.zendesk.com';
  }, []);

  const makeLogin = useCallback(
    async (
      loginData: LoginData,
      cb: Function,
      loginType: ELoginAuthMethod = ELoginAuthMethod.USERNAME_PASSWORD
    ): Promise<void> => {
      if (loginType !== ELoginAuthMethod.USERNAME_PASSWORD) {
        const onLoginSuccess = (loginResponse: any) => {
          handleLoginSuccess(
            loginResponse,
            loginData.rememberMe || false,
            loginData
          );
          cb(true);
        };

        const loginStrategies = {
          [ELoginAuthMethod.MICROSOFT]: loginWithMicrosoft,
          [ELoginAuthMethod.GOOGLE]: loginWithGoogle,
          [ELoginAuthMethod.ACCESS_TOKEN]: loginWithToken
        };

        const strategy = loginStrategies[loginType];
        if (strategy) {
          strategy.mutate(loginData, {
            onSuccess: onLoginSuccess,
            onError: () => {
              cb(false);
            }
          });
        }
        return;
      }

      try {
        const response = await authenticateMfa.mutateAsync({
          email: loginData.userName!,
          password: loginData.password!,
          rememberDevice: false
        });

        if (response.loginResponse) {
          handleLoginSuccess(response.loginResponse, loginData.rememberMe || false, loginData);
          cb(true);
          return;
        }

        mfaFlow.setMfaSession({
          email: loginData.userName!,
          password: loginData.password!,
          rememberMe: loginData.rememberMe || false,
          mfaSessionToken: response.mfaSessionToken,
          maxAttempts: response.maxAttempts,
          currentAttempt: response.currentAttempt
        });

        setModalState(EModalState.MFA_VERIFICATION);
        cb(true);
      } catch (error: any) {
        if (!handleRateLimitError(error, ELockoutType.LOGIN)) {
          cb(false);
        }
      }
    },
    [
      handleLoginSuccess,
      loginWithMicrosoft,
      loginWithGoogle,
      loginWithToken,
      authenticateMfa,
      mfaFlow,
      handleRateLimitError
    ]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const newSessionId = uuidv4();
    localStorageUtil.set(ELocalStorageKeys.SESSION_ID, newSessionId);
    const token = params.get('t');
    if (token) {
      makeLogin(
        {
          accessToken: token,
          rememberMe: false,
          isZendesk: false
        },
        () => {},
        ELoginAuthMethod.ACCESS_TOKEN
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendResetEmail = useCallback(
    async (
      email: string,
      recaptchaToken: string,
      cb: Function
    ): Promise<void> => {
      forgotPassword.mutate(
        { email, recaptchaToken },
        {
          onSuccess: () => {
            setNotificationHandler({
              message: 'An email has been sent successfully.',
              type: EToasterStatus.SUCCESS
            });
            cb();
          },
          onError: (err: any) => {
            setNotificationHandler({
              message:
                err?.response?.data?.message ||
                'An email has not been sent, please try again.',
              type: EToasterStatus.ERROR
            });
            cb();
          }
        }
      );
    },
    [forgotPassword, setNotificationHandler]
  );

  const setNewPassword = useCallback(
    ({
      passwordResetPayload,
      recaptchaToken,
      onComplete
    }: SubmitTokenPasswordResetParams): void => {
      resetPassword.mutate(
        { passwordResetPayload, recaptchaToken },
        {
          onSuccess: () => {
            setNotificationHandler({
              message: 'Password updated successfully.',
              type: EToasterStatus.SUCCESS
            });
            onComplete(true);
          },
          onError: (err: unknown) => {
            const responseMessage = readHttpErrorMessageFromUnknown(err);
            let errorMessage = 'Error in saving password, please try again.';
            if (responseMessage) {
              errorMessage = responseMessage;
            }
            setNotificationHandler({
              message: errorMessage,
              type: EToasterStatus.ERROR
            });
            onComplete(false);
          }
        }
      );
    },
    [resetPassword, setNotificationHandler]
  );

  useEffect(() => {
    if (isLoggedIn()) {
      let navigationPath = '';

      if (permissionsUtil.isAnyOrdersOrPayoutsEnabled(getPermissions())) {
        navigationPath = '/projects';
      } else {
        navigationPath = '/projects';
      }

      navigate(navigationPath);
    }
  }, [getPermissions, isLoggedIn, navigate]);

  useEffect(() => {
    const trackPage = (page: EDashboardPage, additionalData?: { userEmail?: string }) => {
      if (!trackedPagesRef.current.has(page)) {
        trackedPagesRef.current.add(page);
        trackPreLoginEvent({
          page,
          ...additionalData
        });
      }
    };

    if (modalState === EModalState.LOGIN) {
      trackPage(EDashboardPage.LOGIN);
    } else if (modalState === EModalState.MFA_VERIFICATION) {
      trackPage(EDashboardPage.OTP, { userEmail: mfaFlow.mfaState.session.email });
    }
  }, [modalState, mfaFlow.mfaState.session.email, trackPreLoginEvent]);

  const modalStateComponents: Record<EModalState, () => JSX.Element> = {
    [EModalState.LOGIN]: () => (
      <LoginForm
        login={makeLogin}
        setLoginNotification={setNotificationHandler}
        setModalState={setModalStateHandler}
      />
    ),
    [EModalState.MFA_VERIFICATION]: () => (
      <MfaVerificationForm
        email={mfaFlow.mfaState.session.email}
        onVerify={mfaFlow.handleMfaVerify}
        onResend={mfaFlow.handleMfaResend}
        attemptsUsed={mfaFlow.mfaState.attempts.currentAttempt}
        maxAttempts={mfaFlow.mfaState.attempts.maxAttempts}
        isLoading={mfaFlow.isLoading}
      />
    ),
    [EModalState.LOCKOUT]: () => (
      <LockoutScreen
        lockoutEndTime={lockoutState.endTime!}
        onLockoutEnd={handleLockoutEnd}
        onContactSupport={handleContactSupport}
      />
    ),
    [EModalState.FORGET_PASSWORD]: () => (
      <ForgotPasswordForm
        sendResetEmail={sendResetEmail}
        setModalState={setModalState}
      />
    ),
    [EModalState.FINISH_ACCOUNT]: () => (
      <FinishAccountForm
        setNewPassword={setNewPassword}
        setModalState={setModalState}
      />
    ),
    [EModalState.RESET_PASSWORD]: () => (
      <ResetPasswordForm
        setNewPassword={setNewPassword}
        setModalState={setModalState}
      />
    )
  };

  return <div className="w-full">{modalStateComponents[modalState]?.()}</div>;
};

export default LoginModal;
